import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { OwnerDashboardManager } from '../components/OwnerDashboardManager';
import axios from 'axios';
import { 
  Briefcase, 
  Users, 
  MessageSquare, 
  Star, 
  MapPin,
  Clock,
  DollarSign,
  Calendar,
  Zap,
  Crown,
  Shield,
  ShieldCheck
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { applyPageMetadata } from '../utils/seo';
import { getUpgradeCtaConfig } from '../utils/betaUpgradeRules';

function DashboardPage() {
  const { user, isBarOwner, isBartender } = useAuth();
  const [stats, setStats] = useState({
    jobs: 0,
    applications: 0,
    messages: 0,
    reviews: 0
  });
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [availableTonight, setAvailableTonight] = useState(false);
  const [availabilityStatusLoading, setAvailabilityStatusLoading] = useState(true);
  const [updatingAvailability, setUpdatingAvailability] = useState(false);
  const upgradeCtaConfig = getUpgradeCtaConfig({
    isPremium: Boolean(user?.is_premium),
    isBartender,
    isBarOwner,
  });

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch jobs
      const jobsResponse = await axios.get('/jobs?limit=100');
      const allJobs = jobsResponse.data || [];
      const scopedJobs = isBarOwner ? allJobs.filter((job) => job.bar_owner_id === user?.id) : allJobs;
      setRecentJobs(scopedJobs.slice(0, 5));
      
      // Fetch applications
      const applicationsResponse = await axios.get('/applications/my');
      setRecentApplications(applicationsResponse.data);
      
      // Fetch recent notifications
      const notificationsResponse = await axios.get('/notifications?unread_only=true');
      setNotifications(notificationsResponse.data);
      
      // Fetch real message count
      let messageCount = 0;
      try {
        const messagesResponse = await axios.get('/messages/count');
        messageCount = messagesResponse.data.message_count || 0;
      } catch (error) {
        console.error('Failed to fetch message count:', error);
        messageCount = 0;
      }
      
      // Fetch real reviews count
      let reviewsCount = 0;
      try {
        const reviewsResponse = await axios.get('/reviews/stats');
        reviewsCount = reviewsResponse.data.review_count || 0;
      } catch (error) {
        console.error('Failed to fetch reviews count:', error);
        reviewsCount = 0;
      }
      
      if (isBartender) {
        try {
          const availabilityResponse = await axios.get(`/bartenders/${user.id}`);
          const profileData = availabilityResponse.data?.profile || availabilityResponse.data || {};
          setAvailableTonight(Boolean(profileData.available_tonight));
        } catch (error) {
          console.error('Failed to fetch availability status:', error);
          setAvailableTonight(false);
        } finally {
          setAvailabilityStatusLoading(false);
        }
      } else {
        setAvailabilityStatusLoading(false);
      }
      
      // Set REAL stats (no more mock data!)
      setStats({
        jobs: scopedJobs.length,
        applications: applicationsResponse.data.length,
        messages: messageCount, // Real message count
        reviews: reviewsCount   // Real reviews count
      });
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [isBarOwner, isBartender, user?.id]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  useEffect(() => {
    const title = isBarOwner ? 'Owner Dashboard | BarsConnect' : 'Bartender Dashboard | BarsConnect';
    const description = isBarOwner
      ? 'Manage shifts, monitor applicants, and fill open roles quickly on BarsConnect.'
      : 'Track your applications, messages, and opportunities on BarsConnect.';

    const cleanup = applyPageMetadata({
      title,
      description,
      robots: 'noindex,nofollow',
      ogTitle: title,
      ogDescription: description,
      twitterTitle: title,
      twitterDescription: description,
    });

    return cleanup;
  }, [isBarOwner]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleAvailabilityTonightToggle = async (checked) => {
    const previousValue = availableTonight;
    setAvailableTonight(checked);
    setUpdatingAvailability(true);

    try {
      await axios.put('/bartenders/profile/availability-tonight', {
        available_tonight: checked
      });
      toast.success(checked ? 'You are marked as Available Tonight' : 'Available Tonight status turned off');
    } catch (error) {
      console.error('Failed to update availability status:', error);
      setAvailableTonight(previousValue);
      toast.error('Failed to update availability status');
    } finally {
      setUpdatingAvailability(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-loading-state">
        <div className="space-y-3 mb-8">
          <div className="h-10 w-72 rounded-md bg-primary/10 animate-pulse" />
          <div className="h-5 w-96 max-w-full rounded-md bg-white/10 animate-pulse" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="h-28 rounded-xl border border-white/10 bg-white/5 animate-pulse" data-testid={`dashboard-loading-stat-card-${index + 1}`} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="h-72 rounded-xl border border-white/10 bg-white/5 animate-pulse" data-testid="dashboard-loading-panel-left" />
          <div className="h-72 rounded-xl border border-white/10 bg-white/5 animate-pulse" data-testid="dashboard-loading-panel-right" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard">
      {/* Welcome Section */}
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-white mb-2 font-['Barlow_Condensed'] flex items-center">
            Welcome back, {user?.first_name}!
            {user?.is_premium && (
              <Crown className="h-8 w-8 ml-3 text-primary fill-current" title="Premium User" />
            )}
          </h1>
          <p className="text-base md:text-lg text-muted-foreground" data-testid="dashboard-header-subtitle">
            {isBarOwner ? 'Manage your bar operations and find great bartenders' : 'Discover new opportunities and manage your applications'}
            {user?.is_premium && <span className="text-primary ml-2">• Premium Member</span>}
          </p>
          {isBarOwner && (
            <p className="text-sm text-primary/90 mt-1" data-testid="dashboard-instant-shift-hint">
              Tip: Use Instant Shift Fill below for urgent openings and faster responses.
            </p>
          )}
        </div>

        {isBarOwner && user?.is_admin && (
          <Button asChild variant="outline" className="border-primary/30 text-primary hover:bg-primary/10" data-testid="owner-dashboard-admin-quick-access-btn">
            <Link to="/admin/dashboard">
              <Shield className="h-4 w-4 mr-2" />
              Admin Quick Access
            </Link>
          </Button>
        )}
      </div>

      {isBartender && (
        <Card className="bg-white/5 backdrop-blur-md border border-white/5 mb-8" data-testid="available-tonight-status-card">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-xl font-semibold text-white">Available Tonight</h3>
                <p className="text-sm text-muted-foreground">
                  Let bar owners know you are ready for immediate shifts tonight.
                </p>
              </div>

              <div className="flex items-center gap-3">
                {availableTonight && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30" data-testid="dashboard-available-tonight-badge">
                    Available Tonight
                  </Badge>
                )}
                <Switch
                  id="available-tonight-switch"
                  checked={availableTonight}
                  onCheckedChange={handleAvailabilityTonightToggle}
                  disabled={availabilityStatusLoading || updatingAvailability}
                  data-testid="available-tonight-switch"
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {isBarOwner && <OwnerDashboardManager ownerId={user?.id} />}

      {/* Premium Upgrade Banner for non-premium users */}
      {upgradeCtaConfig.show && (
        <Card className="bg-gradient-to-r from-primary/10 to-[#F5C15D]/10 border-primary/20 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-primary/10 rounded-lg">
                  <Star className="h-8 w-8 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white mb-1">Activate Free Beta Upgrade</h3>
                  <p className="text-muted-foreground">
                    {isBarOwner
                      ? 'Enable your free beta owner plan and unlock premium dashboard capabilities during beta.'
                      : 'Enable your free beta bartender plan and unlock premium profile capabilities during beta.'}
                  </p>
                </div>
              </div>
              <Button 
                asChild
                className="bg-primary hover:bg-[hsl(var(--primary-light))] text-white"
                data-testid={upgradeCtaConfig.testId}
              >
                <Link to={upgradeCtaConfig.path}>{upgradeCtaConfig.label}</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid - Now Clickable! */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Link to="/jobs">
          <Card className={`bg-white/5 backdrop-blur-md border border-white/5 hover:border-primary/30 hover:bg-white/10 transition-all cursor-pointer ${user?.is_premium ? 'ring-1 ring-primary/20' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Briefcase className="h-6 w-6 text-primary" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    {isBarOwner ? 'Active Jobs' : 'Available Jobs'}
                  </p>
                  <p className="text-2xl font-bold text-white" data-testid="jobs-count">
                    {stats.jobs}
                    {user?.is_premium && isBarOwner && (
                      <Zap className="inline h-5 w-5 ml-2 text-primary" />
                    )}
                  </p>
                  {stats.jobs === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">Click to browse jobs</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/jobs">
          <Card className={`bg-white/5 backdrop-blur-md border border-white/5 hover:border-primary/30 hover:bg-white/10 transition-all cursor-pointer ${user?.is_premium ? 'ring-1 ring-primary/20' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    {isBarOwner ? 'Applications Received' : 'My Applications'}
                  </p>
                  <p className="text-2xl font-bold text-white" data-testid="applications-count">
                    {stats.applications}
                  </p>
                  {stats.applications === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {isBarOwner ? 'No applications yet' : 'Click to apply to jobs'}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        <Link to="/messages">
          <Card className={`bg-white/5 backdrop-blur-md border border-white/5 hover:border-primary/30 hover:bg-white/10 transition-all cursor-pointer ${user?.is_premium ? 'ring-1 ring-primary/20' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <MessageSquare className="h-6 w-6 text-primary" />
                </div>
                <div className="ml-4 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">
                    Messages
                  </p>
                  <p className="text-2xl font-bold text-white" data-testid="messages-count">
                    {stats.messages}
                  </p>
                  {stats.messages === 0 && (
                    <p className="text-xs text-muted-foreground mt-1">No messages yet</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </Link>

        {stats.reviews > 0 ? (
          <Link to="/profile" data-testid="reviews-card-link">
            <Card className={`bg-white/5 backdrop-blur-md border border-white/5 transition-all hover:border-[#F5C15D]/30 hover:bg-white/10 cursor-pointer ${user?.is_premium ? 'ring-1 ring-primary/20' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-[#F5C15D]/10 rounded-lg">
                    <Star className="h-6 w-6 text-[#F5C15D]" />
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {isBarOwner ? 'Reviews Given' : 'Reviews Received'}
                    </p>
                    <p className="text-2xl font-bold text-white" data-testid="reviews-count">
                      {stats.reviews}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ) : (
          <div data-testid="reviews-card-static">
            <Card className={`bg-white/5 backdrop-blur-md border border-white/5 transition-all ${user?.is_premium ? 'ring-1 ring-primary/20' : ''}`}>
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-[#F5C15D]/10 rounded-lg">
                    <Star className="h-6 w-6 text-[#F5C15D]" />
                  </div>
                  <div className="ml-4 flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {isBarOwner ? 'Reviews Given' : 'Reviews Received'}
                    </p>
                    <p className="text-2xl font-bold text-white" data-testid="reviews-count">
                      {stats.reviews}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {isBarOwner ? 'No reviews given yet' : 'Complete jobs to get reviews'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Jobs with Enhanced Matching */}
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] uppercase">
              {isBarOwner ? 'Your Posted Jobs' : 'Recommended Job Matches'}
            </CardTitle>
            <CardDescription>
              {isBarOwner ? 'Manage your job postings' : 'AI-powered job recommendations based on your profile'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {recentJobs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Briefcase className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No jobs available at the moment</p>
                {isBarOwner && (
                  <Button asChild className="mt-4" data-testid="post-job-btn">
                    <Link to="/jobs">Post Your First Job</Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {recentJobs.slice(0, 3).map((job) => (
                  <div 
                    key={job.id} 
                    className={`p-4 rounded-lg border transition-colors ${
                      job.is_premium ? 'border-primary/30 bg-primary/5' : 'border-white/10 hover:border-white/20'
                    }`}
                    data-testid={`job-${job.id}`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="font-semibold text-white flex items-center">
                        {job.title}
                        {job.is_premium && (
                          <Zap className="h-4 w-4 ml-2 text-primary fill-current" title="Premium Job" />
                        )}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <Badge 
                          variant={job.job_type === 'full_time' ? 'default' : 'secondary'}
                          className="ml-2"
                        >
                          {job.job_type.replace('_', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground mb-2">
                      <MapPin className="h-4 w-4 mr-1" />
                      {job.location?.city || 'Location not specified'}
                      <DollarSign className="h-4 w-4 ml-4 mr-1" />
                      {formatCurrency(job.hourly_rate)}/hr
                    </div>
                    <div className="flex items-center text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-1" />
                      Starts {formatDate(job.start_date)}
                      {job.applications_count > 0 && (
                        <>
                          <Users className="h-4 w-4 ml-4 mr-1" />
                          {job.applications_count} applications
                        </>
                      )}
                    </div>

                    {isBartender && job.bar_profile_snippet && (
                      <div className="mt-3 rounded-md border border-white/10 p-2" data-testid={`dashboard-job-owner-snippet-${job.id}`}>
                        <p className="text-xs font-medium text-white" data-testid={`dashboard-job-owner-business-${job.id}`}>
                          {job.bar_profile_snippet.business_name || job.bar_owner?.name || 'Bar Owner'}
                        </p>
                        {job.bar_profile_snippet.verification_status === 'approved' && (
                          <Badge className="mt-1 bg-emerald-500/20 text-emerald-300 border-emerald-500/30" data-testid={`dashboard-owner-verified-badge-${job.id}`}>
                            <ShieldCheck className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        )}
                        {job.bar_profile_snippet.about_preview && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2" data-testid={`dashboard-job-owner-about-${job.id}`}>
                            {job.bar_profile_snippet.about_preview}
                          </p>
                        )}
                        <Button asChild size="sm" variant="outline" className="mt-2" data-testid={`dashboard-view-bar-profile-btn-${job.id}`}>
                          <Link to={`/bars/${job.bar_owner_id}`}>View Bar Profile</Link>
                        </Button>
                      </div>
                    )}

                  </div>
                ))}
                <Button asChild variant="outline" className="w-full" data-testid="view-all-jobs-btn">
                  <Link to="/jobs">
                    {isBartender ? 'View All Job Matches' : 'View All Jobs'}
                  </Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions / Recent Applications */}
        <Card className="bg-white/5 backdrop-blur-md border border-white/5">
          <CardHeader>
            <CardTitle className="font-['Barlow_Condensed'] uppercase">
              {isBarOwner ? 'Quick Actions' : 'Recent Activity'}
            </CardTitle>
            <CardDescription>
              {isBarOwner ? 'Common tasks and shortcuts' : 'Your recent applications and notifications'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isBarOwner ? (
              <div className="space-y-3">
                <Button asChild variant="outline" className="w-full justify-start" data-testid="browse-bartenders-btn">
                  <Link to="/owner/browse-bartenders">
                    <Users className="mr-2 h-4 w-4" />
                    Browse Bartenders
                  </Link>
                </Button>
                <Button asChild className="w-full justify-start" data-testid="post-new-job-btn">
                  <Link to="/jobs">
                    <Briefcase className="mr-2 h-4 w-4" />
                    Post a Job
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start" data-testid="saved-bartenders-dashboard-btn">
                  <Link to="/saved-bartenders">
                    <Star className="mr-2 h-4 w-4" />
                    Saved Bartenders
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start" data-testid="dashboard-messages-btn">
                  <Link to="/messages">
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Messages
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start" data-testid="account-settings-dashboard-btn">
                  <Link to="/account-settings">
                    <Shield className="mr-2 h-4 w-4" />
                    Account Settings
                  </Link>
                </Button>
                {!user?.is_premium && (
                  <Button asChild variant="outline" className="w-full justify-start bg-primary/10 border-primary/30 hover:bg-primary/20" data-testid="upgrade-free-beta-owner-dashboard-btn">
                    <Link to="/pricing">
                      <Star className="mr-2 h-4 w-4 text-primary" />
                      Activate Free Beta
                    </Link>
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {/* Recent Notifications */}
                {notifications.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-white mb-2">Recent Notifications</h4>
                    <div className="space-y-2">
                      {notifications.slice(0, 3).map((notification) => (
                        <div 
                          key={notification.id}
                          className="p-3 rounded-lg border border-white/10 bg-white/5"
                          data-testid={`notification-${notification.id}`}
                        >
                          <p className="text-sm font-medium text-white">{notification.title}</p>
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {notification.message}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Quick Actions for Bartenders */}
                <div className="space-y-2">
                  <Button asChild className="w-full justify-start" data-testid="browse-jobs-btn">
                    <Link to="/jobs">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Browse Job Matches
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full justify-start" data-testid="update-profile-btn">
                    <Link to="/profile">
                      <Users className="mr-2 h-4 w-4" />
                      Update Profile
                    </Link>
                  </Button>
                </div>
                
                {recentApplications.length === 0 ? (
                  <div className="text-center py-4 text-muted-foreground">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No applications yet</p>
                  </div>
                ) : (
                  <div>
                    <h4 className="text-sm font-medium text-white mb-2">Recent Applications</h4>
                    {recentApplications.slice(0, 2).map((application) => (
                      <div 
                        key={application.id} 
                        className="p-3 rounded-lg border border-white/10 mb-2"
                        data-testid={`application-${application.id}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <p className="text-sm font-semibold text-white">
                            {application.job?.title || 'Job Application'}
                          </p>
                          <Badge 
                            variant={
                              application.status === 'accepted' ? 'default' :
                              application.status === 'rejected' ? 'destructive' : 'secondary'
                            }
                          >
                            {application.status.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex items-center justify-between text-xs text-muted-foreground">
                          <div className="flex items-center">
                            <Clock className="h-3 w-3 mr-1" />
                            Applied {formatDate(application.created_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {recentApplications.length > 0 && (
                  <Button asChild variant="outline" className="w-full" data-testid="view-all-applications-btn">
                    <Link to="/jobs">View All Applications</Link>
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default DashboardPage;
